import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { NotificationAction, NotificationItem } from "@jema-manager/shared";
import { Bell, CheckCheck, Archive, Loader2 } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { ScrollArea } from "@shared/ui/scroll-area";
import { Separator } from "@shared/ui/separator";
import { cn } from "@shared/lib/utils";
import { useNotifications } from "@shared/hooks/notifications/useNotifications";
import { useNotificationRealtime } from "@shared/hooks/notifications/useNotificationRealtime";


const formatGroupKey = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const diffDays = Math.floor((nowStart - dateStart) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return "today";
  if (diffDays === 1) return "yesterday";
  if (diffDays <= 7) return "this-week";
  return "older";
};

const groupLabel = (group: string, i18n: any) => {
  if (group === "today") return i18n._(t({ id: "notifications.group.today", message: 'Today' }));
  if (group === "yesterday") return i18n._(t({ id: "notifications.group.yesterday", message: 'Yesterday' }));
  if (group === "this-week") return i18n._(t({ id: "notifications.group.this-week", message: 'This week' }));
  return i18n._(t({ id: "notifications.group.older", message: 'Older' }));
};

const notificationTitle = (item: NotificationItem, i18n: any) => {
  switch (item.type) {
    case "patient.new_quote_received":
      return i18n._(t({ id: "notifications.patient.new_quote_received.title", message: 'New quote received' }));
    case "patient.travel_plan_ready":
      return i18n._(t({ id: "notifications.patient.travel_plan_ready.title", message: 'Travel plan ready' }));
    case "patient.payment_reminder":
      return i18n._(t({ id: "notifications.patient.payment_reminder.title", message: 'Payment reminder' }));
    case "patient.payment_successful":
      return i18n._(t({ id: "notifications.patient.payment_successful.title", message: 'Payment successful' }));
    case "patient.quote_expiring_soon":
      return i18n._(t({ id: "notifications.patient.quote_expiring_soon.title", message: 'Quote expiring soon' }));
    case "patient.quote_expired":
      return i18n._(t({ id: "notifications.patient.quote_expired.title", message: 'Quote expired' }));
    case "patient.doctor_responded_negotiation":
      return i18n._(
        t({ id: "notifications.patient.doctor_responded_negotiation.title", message: 'Doctor responded' }),
      );
    case "patient.docs_requested":
      return i18n._(t({ id: "notifications.patient.docs_requested.title", message: 'Documents requested' }));
    case "patient.flight_info_requested":
      return i18n._(
        t({ id: "notifications.patient.flight_info_requested.title", message: 'Flight details requested' }),
      );
    case "doctor.patient_accepted_quote":
      return i18n._(t({ id: "notifications.doctor.patient_accepted_quote.title", message: 'Quote accepted' }));
    case "doctor.patient_rejected_quote":
      return i18n._(t({ id: "notifications.doctor.patient_rejected_quote.title", message: 'Quote rejected' }));
    case "doctor.patient_requested_negotiation":
      return i18n._(
        t({ id: "notifications.doctor.patient_requested_negotiation.title", message: 'Negotiation requested' }),
      );
    case "doctor.new_request_to_quote":
      return i18n._(t({ id: "notifications.doctor.new_request_to_quote.title", message: 'New request to quote' }));
    case "doctor.quote_expiring_soon":
      return i18n._(t({ id: "notifications.doctor.quote_expiring_soon.title", message: 'Quote expiring soon' }));
    case "doctor.quote_expired":
      return i18n._(t({ id: "notifications.doctor.quote_expired.title", message: 'Quote expired' }));
    case "doctor.appointment_changed":
      return i18n._(t({ id: "notifications.doctor.appointment_changed.title", message: 'Appointment updated' }));
    case "doctor.admin_action":
      return i18n._(t({ id: "notifications.doctor.admin_action.title", message: 'Admin update' }));
    case "system.superadmin_custom":
      return item.titleKey;
    default:
      return i18n._(t({ id: "notifications.default.title", message: 'Notification' }));
  }
};

const notificationBody = (item: NotificationItem, i18n: any) => {
  if (item.type === "patient.flight_info_requested") {
    return i18n._(
      t({
        id: "notifications.patient.flight_info_requested.body",
        message: 'Admin requested your flight details. Complete the flight form.',
      }),
    );
  }

  if (item.type === "patient.docs_requested") {
    return i18n._(
      t({
        id: "notifications.patient.docs_requested.body",
        message: 'Please upload the requested documents to continue.',
      }),
    );
  }

  if (item.type === "patient.payment_reminder") {
    const amount = item.bodyParams?.outstandingAmount;
    const currency = item.bodyParams?.currency
      ? String(item.bodyParams.currency)
      : i18n._(t({ id: 'notifications.currency.usd', message: 'USD' }));
    const tripNumber = item.bodyParams?.tripNumber;
    return i18n._(
      t({
        id: "notifications.patient.payment_reminder.body",
        message: 'Payment reminder for trip {trip}: {amount} {currency} is due.',
      }),
      {
        trip: tripNumber ?? "-",
        amount: amount ?? "-",
        currency,
      },
    );
  }

  if (item.type === "patient.payment_successful") {
    return i18n._(
      t({
        id: "notifications.patient.payment_successful.body",
        message: 'Your payment was received successfully.',
      }),
    );
  }

  if (item.type === "doctor.patient_accepted_quote") {
    const plan = String(
      item.bodyParams?.acceptedPlanLabel ??
        item.bodyParams?.acceptedPlanKey ??
        i18n._(t({ id: 'notifications.plan.default', message: 'Plan' })),
    ).trim();
    const total = item.bodyParams?.acceptedGrandTotal;
    return i18n._(
      t({
        id: "notifications.doctor.patient_accepted_quote.body",
        message: 'Patient accepted the {plan} plan{amount}.',
      }),
      {
        plan,
        amount: total != null ? ` - ${String(total)}` : "",
      },
    );
  }

  if (item.type === "doctor.patient_rejected_quote") {
    const reason = item.bodyParams?.reason ? String(item.bodyParams.reason) : "";
    return reason || i18n._(t({ id: "notifications.doctor.patient_rejected_quote.body", message: 'Patient rejected your quote.' }));
  }

  if (item.type === "doctor.patient_requested_negotiation") {
    return ((item.bodyParams?.notes ? String(item.bodyParams.notes) : "") || i18n._(t({ id: "notifications.doctor.patient_requested_negotiation.body", message: 'Patient requested negotiation.' })));
  }

  if (item.type === "system.superadmin_custom") {
    return item.bodyKey;
  }

  return i18n._(t({ id: "notifications.default.body", message: 'Open to view details.' }));
};

const notificationActionVariant = (action: NotificationAction): "default" | "secondary" | "destructive" | "ghost" => {
  if (action.variant === "primary") return "default";
  if (action.variant === "danger") return "destructive";
  if (action.variant === "ghost") return "ghost";
  return "secondary";
};

type NotificationCenterProps = {
  children: React.ReactNode;
};

export const NotificationCenter: React.FC<NotificationCenterProps> = ({ children }) => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [isArchivedView, setIsArchivedView] = React.useState(false);
  const prevUnreadCountRef = React.useRef(0);

  useNotificationRealtime({ enabled: true });
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);
  const INACTIVITY_TIMEOUT_MS = 10_000;

  const {
    notifications,
    unreadCount,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    markAsRead,
    markAllAsRead,
    archive,
  } = useNotifications({
    category: undefined,
    unreadOnly: undefined,
    includeArchived: isArchivedView,
    enabled: open,
  });

  const displayNotifications = React.useMemo(() => {
    return notifications.filter(n => isArchivedView ? n.archivedAt : !n.archivedAt);
  }, [notifications, isArchivedView]);


  React.useEffect(() => {
    const normalizedPath = location.pathname.toLowerCase();
    const isHomeOrMyDay =
      normalizedPath === "/dashboard" ||
      normalizedPath.endsWith("/dashboard") ||
      normalizedPath.includes("/my-day");

    // Auto-open only on home/my-day when unread notifications increase.
    if (isHomeOrMyDay && unreadCount > prevUnreadCountRef.current) {
      if (!open) setOpen(true);
    }
    prevUnreadCountRef.current = unreadCount;
  }, [location.pathname, unreadCount, open]);

  React.useEffect(() => {
    if (!open) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      return;
    }

    const restartInactivityTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setOpen(false);
      }, INACTIVITY_TIMEOUT_MS);
    };

    restartInactivityTimer();

    const inactivityEvents: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];
    inactivityEvents.forEach((eventName) => {
      window.addEventListener(eventName, restartInactivityTimer, { passive: true });
    });

    return () => {
      inactivityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, restartInactivityTimer);
      });
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [open]);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const grouped = React.useMemo(() => {
    const map = new Map<string, NotificationItem[]>();
    displayNotifications.forEach((item) => {
      const key = formatGroupKey(item.lastOccurredAt);
      const existing = map.get(key) ?? [];
      existing.push(item);
      map.set(key, existing);
    });
    return map;
  }, [displayNotifications]);

  const handleOpenItem = async (item: NotificationItem) => {
    if (!item.isRead) {
      await markAsRead(item.id);
    }
    let nextUrl = item.actionUrl;
    try {
      const parsed = new URL(item.actionUrl, window.location.origin);
      if (!parsed.searchParams.get("notificationId")) {
        parsed.searchParams.set("notificationId", item.id);
      }
      nextUrl = `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      // keep original action url
    }
    setOpen(false);
    navigate(nextUrl);
  };

  const resolveActionUrl = (item: NotificationItem, action?: NotificationAction) => {
    const rawUrl = action?.actionUrl || item.actionUrl;
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      if (!parsed.searchParams.get("notificationId")) {
        parsed.searchParams.set("notificationId", item.id);
      }
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return rawUrl;
    }
  };

  const handleNotificationAction = async (
    event: React.MouseEvent,
    item: NotificationItem,
    action: NotificationAction,
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (action.markAsRead !== false && !item.isRead) {
      await markAsRead(item.id);
    }

    const nextUrl = resolveActionUrl(item, action);
    if (action.dismissAfterAction !== false) {
      setOpen(false);
    }
    navigate(nextUrl);
  };


  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={12}
        className="w-[320px] sm:w-[380px] p-0 rounded-2xl overflow-hidden border border-border/70 bg-popover/95 text-popover-foreground shadow-2xl ring-1 ring-inset ring-border/60 backdrop-blur-md"
      >
        <div className="px-5 pt-4 pb-3 relative">
          <div className="absolute inset-0 bg-gradient-to-b from-muted/40 to-transparent pointer-events-none" />
          <div className="relative flex items-center justify-between font-medium">
            <div className="flex items-center gap-1 rounded-lg bg-muted/70 p-1 ring-1 ring-inset ring-border/60">
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 px-3 text-xs font-medium rounded-md transition-all", !isArchivedView ? "bg-background text-foreground shadow-sm ring-1 ring-inset ring-border/70" : "text-muted-foreground hover:text-foreground hover:bg-background/70")}
                onClick={() => setIsArchivedView(false)}
              >
                <Trans id="notifications.filter.inbox">Inbox</Trans>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-7 px-3 text-xs font-medium rounded-md transition-all", isArchivedView ? "bg-background text-foreground shadow-sm ring-1 ring-inset ring-border/70" : "text-muted-foreground hover:text-foreground hover:bg-background/70")}
                onClick={() => setIsArchivedView(true)}
              >
                <Trans id="notifications.filter.archived">Archived</Trans>
              </Button>
            </div>
            <div className="flex items-center gap-2">
              {!isArchivedView && unreadCount > 0 && <Badge variant="secondary" className="font-medium border-0 shadow-sm bg-brand-blue text-white hover:bg-brand-blue/90">{unreadCount}</Badge>}
              {!isArchivedView && (
                <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-500 dark:text-slate-400 hover:text-brand-blue dark:hover:text-white hover:bg-brand-blue/10 dark:hover:bg-white/10" onClick={() => void markAllAsRead()} title={i18n._(t({ id: "notifications.action.mark-all-read", message: 'Mark all read' }))}>
                  <CheckCheck className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        <Separator className="bg-border/80" />

        <ScrollArea className="h-[360px]">
          <div className="pb-2">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center pt-20 pb-10 text-slate-600 dark:text-slate-400">
                <Loader2 className="h-6 w-6 animate-spin text-brand-blue/60 dark:text-white/20 mb-3" />
                <span className="text-xs tracking-wide font-medium"><Trans id="notifications.loading">Loading notifications...</Trans></span>
              </div>
            ) : displayNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center pt-32 pb-10 text-slate-500 dark:text-slate-500">
                {isArchivedView ? <Archive className="h-10 w-10 text-black/10 dark:text-white/10 mb-4 drop-shadow-sm" /> : <Bell className="h-10 w-10 text-black/10 dark:text-white/10 mb-4 drop-shadow-sm" />}
                <p className="text-sm">
                  {isArchivedView ? (
                    <Trans id="notifications.empty.archived">No archived notifications.</Trans>
                  ) : (
                    <Trans id="notifications.empty.inbox">All caught up! No notifications.</Trans>
                  )}
                </p>
              </div>
            ) : (
              ["today", "yesterday", "this-week", "older"]
                .filter((group) => (grouped.get(group) ?? []).length > 0)
                .map((group) => (
                  <section key={group} aria-label={groupLabel(group, i18n)}>
                    <h3 className="sticky top-0 z-10 bg-popover/95 px-5 py-1.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground border-b border-border/70 shadow-sm backdrop-blur-md">
                      {groupLabel(group, i18n)}
                    </h3>
                    <div className="flex flex-col">
                      {(grouped.get(group) ?? []).map((item) => (
                        <div
                          key={item.id}
                          className={cn(
                            "group relative flex flex-col items-start gap-1 justify-center border-b border-border/60 p-4 pl-5 transition-all hover:bg-muted/35",
                            !item.isRead ? "bg-brand-blue/[0.06] dark:bg-brand-blue/[0.12]" : ""
                          )}
                        >
                          {!item.isRead && (
                            <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[60%] w-[3px] rounded-r-full bg-brand-blue shadow-[0_0_8px_rgba(59,130,246,0.5)] dark:shadow-[0_0_8px_rgba(var(--brand-blue-rgb),0.8)]" />
                          )}
                          <button className="w-full text-left outline-none" onClick={() => void handleOpenItem(item)}>
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-1 sm:gap-4 lg:gap-2">
                              <p className={cn("text-13px font-semibold", item.isRead ? "text-slate-600 dark:text-slate-300" : "text-slate-900 dark:text-white")}>{notificationTitle(item, i18n)}</p>
                              <div className="flex items-center gap-1 shrink-0 mt-0.5 sm:mt-0">
                                {item.occurrenceCount > 1 ? <Badge variant="outline" className="border-border/70 bg-background/80 text-muted-foreground hover:bg-muted/70 text-[9px] px-1.5 h-4 min-w-4 p-0 shadow-sm"><Trans id='shared.layout.NotificationCenter.x'>x{item.occurrenceCount}</Trans></Badge> : null}
                                <span className={cn("text-[10px] whitespace-nowrap", item.isRead ? "text-slate-500 dark:text-slate-500" : "text-brand-blue font-semibold drop-shadow-sm")}>
                                  {new Date(item.lastOccurredAt).toLocaleString(undefined, { hour: 'numeric', minute: 'numeric', month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            </div>
                            <p className={cn("mt-1.5 text-[12px] pr-6", item.isRead ? "text-slate-500" : "text-slate-600 dark:text-slate-300 leading-relaxed")}>{notificationBody(item, i18n)}</p>
                          </button>

                          {item.actions && item.actions.length > 0 && (
                            <div className="mt-3.5 flex flex-wrap items-center gap-2">
                              {item.actions.map((action) => (
                                <Button
                                  key={action.id}
                                  size="sm"
                                  variant={notificationActionVariant(action)}
                                  className={cn(
                                    "h-7 text-xs px-3 rounded-md transition-all",
                                    action.variant === 'ghost' ? 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-white/10' :
                                      action.variant === 'primary' ? 'bg-brand-blue hover:bg-brand-blue/90 text-white border-none shadow-md shadow-brand-blue/20 dark:shadow-black/30 ring-1 ring-inset ring-brand-blue/50 dark:ring-white/10' : 'bg-white/60 dark:bg-white/5 hover:bg-white/80 dark:hover:bg-white/10 text-slate-800 dark:text-slate-200 border-white/60 dark:border-white/10 shadow-sm ring-1 ring-inset ring-white/80 dark:ring-0'
                                  )}
                                  onClick={(event) => {
                                    void handleNotificationAction(event, item, action);
                                  }}
                                >
                                  {action.fallbackLabel ?? action.labelKey}
                                </Button>
                              ))}
                            </div>
                          )}

                          <div className={cn(
                            "absolute right-2 top-2 sm:top-1/2 sm:-translate-y-1/2 flex items-center gap-1 rounded-lg border border-border/70 bg-popover/95 p-1 shadow-lg backdrop-blur-md transition-all duration-200 ring-1 ring-inset ring-border/70",
                            "opacity-0 group-hover:opacity-100 sm:translate-x-2 sm:group-hover:translate-x-0",
                            "focus-within:opacity-100 focus-within:translate-x-0"
                          )}>
                            {!item.isRead && !isArchivedView && (
                              <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm text-slate-500 dark:text-slate-400 hover:text-brand-blue dark:hover:text-white hover:bg-brand-blue/10 dark:hover:bg-white/10" onClick={() => void markAsRead(item.id)} title={t({
                                id: 'shared.layout.NotificationCenter.mark-as-read',

                                message: 'Mark as read',
                              })}>
                                <CheckCheck className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            {!isArchivedView && (
                              <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm text-slate-500 dark:text-slate-400 hover:text-brand-blue dark:hover:text-white hover:bg-brand-blue/10 dark:hover:bg-white/10" onClick={() => void archive(item.id)} title={t({
                                id: 'shared.layout.NotificationCenter.archive',

                                message: 'Archive',
                              })}>
                                <Archive className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                ))
            )}
            {hasNextPage ? (
              <div className="px-4 mt-3">
                <Button
                  variant="ghost"
                  className="w-full rounded-md text-xs text-brand-blue hover:text-brand-blue/80 hover:bg-brand-blue/10 transition-all"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <Trans id="notifications.action.loading-more">Loading history...</Trans>
                  ) : (
                    <Trans id="notifications.action.load-more">Load previous notifications</Trans>
                  )}
                </Button>
              </div>
            ) : null}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
